import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  EyeOff,
  FileLock2,
  FileText,
  Menu,
  Minus,
  Shield,
  ShieldCheck,
  UserCheck,
  X,
} from 'lucide-react';
import Footer from '../../components/Footer';
import CareerGrowthLogo from '../../components/CareerGrowthLogo';

const SIGNUP_PATH = '/b2b/auth?signup=1';
const SIGNIN_PATH = '/b2b/auth';
const LANDING_PATH = '/for-recruiters';

/**
 * /b2b/compliance — public marketing page positioning Jobskills as EU AI Act-native.
 *
 * Linked from:
 *   - ForRecruitersLandingScreen.jsx Compliance section CTA
 *   - b2b/PricingScreen.jsx CompliancePerks footer link
 *   - Footer (deeper nav)
 *
 * Content sources:
 *   - _docs/marketing/eu-ai-act-readiness-and-positioning.md (source-of-truth)
 *   - competitor-profiles/_summary.md (comparison data)
 */
export default function ComplianceScreen() {
  return (
    <div className="min-h-screen bg-canvas text-ink-1 font-sans">
      <Nav />
      <main>
        <Hero />
        <PillarGrid />
        <CompetitorTable />
        <ReadinessState />
        <CompliancePack />
        <ProcurementFAQ />
        <CtaStrip />
      </main>
      <Footer />
    </div>
  );
}

/* ─────────────────────────  NAV  ───────────────────────── */

function Nav() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKey = (e) => e.key === 'Escape' && setIsOpen(false);
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const close = () => setIsOpen(false);

  return (
    <>
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-5 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <Link to={LANDING_PATH} className="flex items-center gap-3 min-w-0">
            <CareerGrowthLogo size={36} />
            <span className="hidden sm:inline-flex text-[10px] uppercase tracking-wider font-bold text-accent border border-accent/30 rounded-full px-2 py-0.5">
              Compliance
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link to={LANDING_PATH} className="text-sm font-semibold text-ink-2 hover:text-ink-1 px-3 py-2 rounded-lg transition">
              For recruiters
            </Link>
            <Link to={SIGNIN_PATH} className="text-sm font-semibold text-ink-2 hover:text-ink-1 px-3 py-2 rounded-lg transition">
              Sign in
            </Link>
            <Link
              to={SIGNUP_PATH}
              className="ml-2 inline-flex items-center gap-1.5 text-sm font-bold bg-accent hover:bg-accent-hover text-white px-4 py-2.5 rounded-2xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              Start free
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setIsOpen(true)}
            className="md:hidden h-10 w-10 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            aria-label="Open menu"
            aria-expanded={isOpen}
          >
            <Menu className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
      </header>

      <div
        className={`md:hidden fixed inset-0 z-[1000] bg-black/45 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={close}
        aria-hidden={!isOpen}
      />

      <aside
        className={`md:hidden fixed inset-y-0 right-0 z-[1001] w-[85%] max-w-sm bg-white shadow-2xl transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!isOpen}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <span className="text-sm font-bold text-ink-1">Menu</span>
          <button
            type="button"
            onClick={close}
            className="h-10 w-10 rounded-xl border border-gray-200 flex items-center justify-center text-ink-2"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
        <nav className="flex flex-col p-5 gap-2">
          <Link to={LANDING_PATH} onClick={close} className="px-3 py-3 rounded-xl text-sm font-semibold text-ink-1 hover:bg-canvas">
            For recruiters
          </Link>
          <Link to={SIGNIN_PATH} onClick={close} className="px-3 py-3 rounded-xl text-sm font-semibold text-ink-1 hover:bg-canvas">
            Sign in
          </Link>
          <Link to={SIGNUP_PATH} onClick={close} className="mt-2 inline-flex items-center justify-center gap-1.5 text-sm font-bold bg-accent hover:bg-accent-hover text-white px-4 py-3 rounded-2xl">
            Start free
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </nav>
      </aside>
    </>
  );
}

/* ─────────────────────────  HERO  ───────────────────────── */

function Hero() {
  return (
    <section className="bg-gradient-to-b from-white to-canvas border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 text-accent px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
          <Shield className="w-3.5 h-3.5" aria-hidden="true" />
          EU AI Act · GDPR · CCPA
        </div>
        <h1 className="mt-5 text-3xl md:text-5xl font-bold leading-tight max-w-4xl">
          Built for the EU AI Act, not retrofitted for it.
        </h1>
        <p className="mt-5 text-base md:text-xl text-ink-2 leading-relaxed max-w-3xl">
          The EU AI Act enters its high-risk phase for hiring AI on{' '}
          <strong className="text-ink-1">2 August 2026</strong>. Most AI hiring tools were
          designed before this regulation existed. Jobskills was designed for it —
          closed-ended text screening, no biometric scoring, AI as recommendation with
          your recruiter in the loop, tamper-evident audit trail, versioned consent.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <a
            href="#compliance-pack"
            className="inline-flex items-center gap-1.5 text-sm font-bold bg-accent hover:bg-accent-hover text-white px-5 py-3 rounded-2xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            Get the Compliance Pack
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
          <a
            href="#comparison"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-ink-1 bg-white border border-gray-200 hover:border-gray-300 px-5 py-3 rounded-2xl transition"
          >
            Compare to other tools
            <ChevronDown className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>

        {/* Quick stats */}
        <dl className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {[
            { label: 'Days until high-risk obligations', value: '~70' },
            { label: 'Biometric data processed', value: 'None' },
            { label: 'Audit log retention', value: '18 mo' },
            { label: 'Legal docs published', value: '16' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-gray-200 p-4 md:p-5">
              <dt className="text-[10px] uppercase tracking-wider font-bold text-ink-3">{s.label}</dt>
              <dd className="mt-1 text-2xl md:text-3xl font-bold text-ink-1">{s.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/* ─────────────────────────  PILLARS  ───────────────────────── */

const PILLARS = [
  {
    icon: EyeOff,
    title: 'No biometric scoring',
    body:
      'We don’t analyze faces, voices, or "confidence levels." Structured text questions, structured text answers. Candidate identity is verified by email OTP only.',
    evidence: 'Code-level guarantee: no facial/voice/emotion analysis in the AI pipeline.',
    cite: 'EU AI Act Art 5(1)(f)',
  },
  {
    icon: UserCheck,
    title: 'AI recommends, your recruiter decides',
    body:
      'Fit verdicts come with reasoning and evidence. No automated hire/reject. The recruiter scores every candidate. Optional independent bar-raiser scoring with variance tracking.',
    evidence:
      'Implementation: AI returns {verdict, reasoning}; recruiter scores via BARS panel; bar-raiser calibration enforced in DB.',
    cite: 'GDPR Art 22 · EU AI Act Art 14',
  },
  {
    icon: FileLock2,
    title: 'Tamper-evident audit trail',
    body:
      'Every AI call is logged with SHA-256 hashes of inputs and outputs, model version, latency, and outcome. Audit-grade record without retaining full prompt or response content.',
    evidence:
      'Implementation: b2b.ai_audit_logs table; every agent call recorded via centralized AuditContext.',
    cite: 'EU AI Act Art 12 · GDPR Art 30',
  },
  {
    icon: BadgeCheck,
    title: 'Versioned candidate consent',
    body:
      'Candidates explicitly consent to AI processing before any analysis runs. Consent version is captured per screening. When the policy changes, re-consent is required.',
    evidence:
      'Implementation: b2b.screenings.candidate_consent_at + candidate_consent_version columns enforced at endpoint level.',
    cite: 'GDPR Art 7 · EU AI Act Art 50',
  },
  {
    icon: Calendar,
    title: 'Configurable data retention',
    body:
      'Choose 6, 12, 24, or 36 months per workspace. After the window, candidate data is automatically deleted by a daily Celery task. Audit logs retained 18 months for compliance, then purged.',
    evidence:
      'Implementation: b2b.workspaces.data_retention_months; purge_expired_b2b_screenings + purge_expired_b2b_audit_logs cron tasks.',
    cite: 'GDPR Art 5(1)(e)',
  },
  {
    icon: FileText,
    title: 'Compliance pack at signup',
    body:
      'Data Processing Agreement (with SCCs Module 2 + UK IDTA), AI System Card, sub-processor list, breach runbook, and DPIA template — all customer-accessible from day one.',
    evidence: '16 legal documents published in /privacy, /b2b/dpa, /b2b/terms, /ai, and /privacy/sub-processors.',
    cite: 'GDPR Art 28 · EU AI Act Art 50',
  },
];

function PillarGrid() {
  return (
    <section className="bg-white border-y border-gray-100">
      <div className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
        <p className="text-xs font-bold uppercase tracking-wider text-accent">Six pillars</p>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold max-w-3xl leading-tight">
          The architecture, not just the marketing.
        </h2>
        <p className="mt-5 text-base md:text-lg text-ink-2 max-w-3xl leading-relaxed">
          Compliance is mostly invisible work. Below is the work, with citations to the
          regulation that asks for it and pointers to the code that implements it.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="bg-white rounded-2xl border border-gray-200 p-6 md:p-7 shadow-card flex flex-col"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                    <Icon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-ink-1">{p.title}</h3>
                    <p className="mt-2 text-sm md:text-base text-ink-2 leading-relaxed">{p.body}</p>
                  </div>
                </div>
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <p className="text-xs text-ink-3 leading-relaxed">
                    <span className="font-bold text-ink-2">How it works:</span> {p.evidence}
                  </p>
                  <p className="mt-2 text-[10px] uppercase tracking-wider font-bold text-ink-3">{p.cite}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  COMPETITOR COMPARISON  ───────────────────────── */

const COMPARISON_HEADERS = ['Capability', 'Jobskills B2B', 'Interviewer.AI', 'HireVue', 'Skillora', 'Metaview'];

const COMPARISON_ROWS = [
  {
    cap: 'Facial expression analysis',
    cells: ['no', 'risk', 'removed', 'partial', 'partial'],
    note: ['Text-only', 'Yes (high-risk)', 'Removed 2021 (FTC pressure)', 'Has proctoring', 'Records video'],
  },
  {
    cap: 'Voice biometric / tone analysis',
    cells: ['no', 'risk', 'risk', 'partial', 'partial'],
    note: ['Not used', 'Yes', 'Voice analysis', 'Proctoring', 'Records audio'],
  },
  {
    cap: 'Designed for EU AI Act Annex III',
    cells: ['yes', 'unclear', 'unclear', 'no', 'unclear'],
    note: ['By design', '—', '—', 'Not documented', '—'],
  },
  {
    cap: 'GDPR Art 22 — no autonomous decisions',
    cells: ['yes', 'risk', 'risk', 'no', 'na'],
    note: ['AI recommends, recruiter scores', 'AI scoring', 'AI scoring', 'Auto-recommend', 'N/A (note-taker)'],
  },
  {
    cap: 'Explicit EU AI Act consent flow',
    cells: ['yes', 'risk', 'risk', 'no', 'na'],
    note: ['Versioned + re-consent', 'Generic ToS', 'Generic ToS', 'Not visible', 'N/A'],
  },
  {
    cap: 'Audit logs with cryptographic hashes',
    cells: ['yes', 'unclear', 'unclear', 'no', 'unclear'],
    note: ['SHA-256 per call', '—', '—', '—', '—'],
  },
  {
    cap: 'Published sub-processor list',
    cells: ['yes', 'unclear', 'yes', 'no', 'yes'],
    note: ['9 vendors', '—', 'Yes', '—', 'Yes'],
  },
  {
    cap: 'Customer-ready DPIA template',
    cells: ['yes', 'no', 'partial', 'no', 'unclear'],
    note: ['In Compliance Pack', '—', 'On request', '—', '—'],
  },
  {
    cap: 'Independent bar-raiser scoring',
    cells: ['yes', 'no', 'no', 'no', 'no'],
    note: ['BARS + variance', '—', '—', '—', '—'],
  },
  {
    cap: 'Closed-ended only (no free-form video Q&A)',
    cells: ['yes', 'no', 'no', 'partial', 'na'],
    note: ['By design', 'Video-based', 'Video-based', 'Mixed', 'N/A'],
  },
];

function StatusCell({ value, note }) {
  let icon = null;
  let className = 'text-ink-3';
  if (value === 'yes') {
    icon = <CheckCircle2 className="w-4 h-4 text-emerald-600" aria-hidden="true" />;
    className = 'text-emerald-700 font-semibold';
  } else if (value === 'no') {
    icon = <CheckCircle2 className="w-4 h-4 text-emerald-600" aria-hidden="true" />;
    className = 'text-emerald-700 font-semibold';
  } else if (value === 'risk') {
    icon = <X className="w-4 h-4 text-red-500" aria-hidden="true" />;
    className = 'text-red-600 font-semibold';
  } else if (value === 'partial') {
    icon = <Minus className="w-4 h-4 text-amber-500" aria-hidden="true" />;
    className = 'text-amber-700 font-semibold';
  } else if (value === 'removed') {
    icon = <Clock className="w-4 h-4 text-ink-3" aria-hidden="true" />;
    className = 'text-ink-2 font-semibold';
  } else if (value === 'unclear') {
    icon = <Minus className="w-4 h-4 text-ink-3" aria-hidden="true" />;
    className = 'text-ink-3 font-semibold';
  } else if (value === 'na') {
    icon = <Minus className="w-4 h-4 text-ink-3" aria-hidden="true" />;
    className = 'text-ink-3 font-medium';
  }
  return (
    <div className="flex items-start gap-1.5">
      {icon}
      <div>
        <span className={`text-xs ${className}`}>{note}</span>
      </div>
    </div>
  );
}

function CompetitorTable() {
  return (
    <section id="comparison" className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
      <p className="text-xs font-bold uppercase tracking-wider text-accent">Comparison</p>
      <h2 className="mt-3 text-3xl md:text-4xl font-bold max-w-3xl leading-tight">
        How Jobskills compares on compliance.
      </h2>
      <p className="mt-5 text-base text-ink-2 max-w-3xl leading-relaxed">
        Recruiter-side AI tools profiled 2026-05-26 from public information, vendor
        documentation, and competitor profiles. Marks reflect documented behaviour; we
        note "unclear" where the vendor doesn't disclose. Updated as the picture changes.
      </p>

      <div className="hidden lg:block mt-10 rounded-2xl border border-gray-200 overflow-x-auto shadow-card bg-white">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-[11px] font-bold uppercase tracking-wider text-ink-3">
              {COMPARISON_HEADERS.map((h, i) => (
                <th key={h} className={`py-3 px-4 ${i === 1 ? 'bg-accent/10 text-accent' : ''}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARISON_ROWS.map((row) => (
              <tr key={row.cap} className="border-t border-gray-100">
                <td className="py-4 px-4 font-semibold text-ink-1 align-top w-1/4">{row.cap}</td>
                {row.cells.map((value, i) => (
                  <td key={i} className={`py-4 px-4 align-top ${i === 0 ? 'bg-accent/5 border-l-4 border-accent' : ''}`}>
                    <StatusCell value={value} note={row.note[i]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile / tablet: cards per row */}
      <div className="lg:hidden mt-8 space-y-3">
        {COMPARISON_ROWS.map((row) => (
          <div key={row.cap} className="rounded-2xl border border-gray-200 bg-white p-4">
            <p className="text-sm font-bold text-ink-1">{row.cap}</p>
            <ul className="mt-3 space-y-2">
              {COMPARISON_HEADERS.slice(1).map((h, i) => (
                <li key={h} className={`flex items-start justify-between gap-3 text-xs ${i === 0 ? 'pb-2 mb-1 border-b border-gray-100' : ''}`}>
                  <span className={`${i === 0 ? 'font-bold text-accent' : 'font-semibold text-ink-2'}`}>{h}</span>
                  <span className="text-right shrink-0">
                    <StatusCell value={row.cells[i]} note={row.note[i]} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <p className="mt-6 text-xs text-ink-3 max-w-3xl">
        Sources: jobskills.work code review (2026-05-26); vendor homepages, DPA pages,
        and AI System Cards as of 2026-05; <Link to="/blog" className="underline">competitor analysis</Link>.
        Last updated 2026-05-26.
      </p>
    </section>
  );
}

/* ─────────────────────────  READINESS STATE (TRANSPARENCY)  ───────────────────────── */

function ReadinessState() {
  const now = [
    'AI generates recommendations with reasoning; recruiter scores and decides.',
    'No facial or voice biometric processing — text-only screening.',
    'SHA-256 audit log per AI call: input hash, output hash, model, latency, outcome.',
    'Versioned candidate consent with email OTP identity verification.',
    'Configurable data retention (6 / 12 / 24 / 36 mo) with automatic deletion.',
    'B2B Data Processing Agreement published with SCCs Module 2 + UK IDTA.',
    'Sub-processor list (9 vendors) with transfer mechanisms documented.',
    'Breach runbook with 72-hour Art 33 notification template.',
    'Right-to-withdraw at consent step; decline modal with optional reason.',
    'C2C account export (GDPR Art 20) and 30-day grace deletion (Art 17).',
  ];

  const coming = [
    {
      label: 'Outside counsel legal review',
      eta: 'Q2 2026',
      detail: 'All 16 legal documents currently marked DRAFT v2026-05. Outside counsel engagement in progress.',
    },
    {
      label: 'EU & UK Article 27 representative',
      eta: 'Q2 2026',
      detail: 'Required for non-EU/UK controllers. Prighter/EDPO procurement in progress.',
    },
    {
      label: 'Signed DPIA on file',
      eta: 'Q2 2026',
      detail: 'Internal DPIA + customer-ready DPIA template, both signed off by outside counsel.',
    },
    {
      label: 'Sub-processor SCC copies on file',
      eta: 'Q2 2026',
      detail: 'SCC copies requested from DeepInfra, Railway, Sentry, Amplitude, Resend.',
    },
    {
      label: 'B2B candidate Art 20 / Art 17 endpoints',
      eta: 'Q3 2026',
      detail: 'Self-service data export and erasure request endpoints for screening candidates.',
    },
    {
      label: 'Bias / fairness assessment (Fairlearn)',
      eta: 'Q3 2026',
      detail: 'Llama 3.3 70B pipeline tested via open-source fairness toolkit; summary published.',
    },
  ];

  return (
    <section className="bg-white border-y border-gray-100">
      <div className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
        <p className="text-xs font-bold uppercase tracking-wider text-accent">Readiness</p>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold max-w-3xl leading-tight">
          Defensible today. Continuing to harden.
        </h2>
        <p className="mt-5 text-base text-ink-2 max-w-3xl leading-relaxed">
          Compliance is a continuous program, not a one-time claim. Here is what we can
          defend today, in writing, with current evidence — and here is what's actively
          in progress before the EU AI Act high-risk provisions activate 2 August 2026.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-700" aria-hidden="true" />
              <h3 className="text-base md:text-lg font-bold text-emerald-900">Defensible now</h3>
            </div>
            <p className="mt-2 text-sm text-emerald-900/80">Built and shipping in production. Verifiable from code + published legal docs.</p>
            <ul className="mt-5 space-y-2.5">
              {now.map((s) => (
                <li key={s} className="flex items-start gap-2.5 text-sm text-emerald-950 leading-relaxed">
                  <Check className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0" aria-hidden="true" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-700" aria-hidden="true" />
              <h3 className="text-base md:text-lg font-bold text-amber-900">In progress before 2 Aug 2026</h3>
            </div>
            <p className="mt-2 text-sm text-amber-900/80">Procurement, legal review, and code work scheduled before EU AI Act high-risk obligations activate.</p>
            <ul className="mt-5 space-y-3">
              {coming.map((c) => (
                <li key={c.label} className="text-sm text-amber-950 leading-relaxed">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold">{c.label}</span>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 shrink-0">{c.eta}</span>
                  </div>
                  <p className="mt-1 text-xs text-amber-900/80">{c.detail}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  COMPLIANCE PACK  ───────────────────────── */

const PACK_CONTENTS = [
  { name: 'Data Processing Agreement (B2B)', detail: 'SCCs Module 2 + UK IDTA + TOM annex + sub-processor annex', href: '/b2b/dpa' },
  { name: 'AI Transparency Notice (System Card)', detail: 'EU AI Act Art 50 ready', href: '/ai' },
  { name: 'Sub-processors List', detail: '9 vendors with transfer mechanisms and DPA status', href: '/privacy/sub-processors' },
  { name: 'Privacy Policy', detail: 'GDPR Art 13/14 + CCPA', href: '/privacy' },
  { name: 'Acceptable Use Policy', detail: 'Prohibited content + non-discrimination', href: '/aup' },
  { name: 'Candidate Privacy Notice', detail: 'For /screen/:token candidates', href: '/screen/privacy' },
  { name: 'B2B Business Terms', detail: 'Service terms + employment law compliance', href: '/b2b/terms' },
  { name: 'Cookie Policy', detail: 'ePrivacy + GDPR consent + CCPA share opt-out', href: '/cookies' },
  { name: 'Client DPIA template', detail: 'Fillable DPIA your DPO can complete', href: 'mailto:b2b@jobskills.work?subject=DPIA%20template%20request' },
];

function CompliancePack() {
  return (
    <section id="compliance-pack" className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
      <div className="rounded-3xl bg-ink-1 text-white overflow-hidden shadow-card">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="p-8 md:p-10 lg:p-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
              <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
              Compliance Pack
            </div>
            <h2 className="mt-5 text-3xl md:text-4xl font-bold leading-tight">
              Everything your procurement team needs. In one bundle.
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/80 leading-relaxed">
              Nine customer-accessible documents, available without sales gating. We don't
              hide compliance behind enterprise tiers or NDA-walled vault tours.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:b2b@jobskills.work?subject=Compliance%20Pack%20request&body=Hi%20%E2%80%94%20please%20send%20me%20the%20Jobskills%20Compliance%20Pack.%20Our%20use%20case%3A%20%5Bbrief%20description%5D.%20Thanks."
                className="inline-flex items-center gap-1.5 text-sm font-bold bg-accent hover:bg-accent-hover text-white px-5 py-3 rounded-2xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-ink-1"
              >
                Email me the pack
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
              <Link
                to={SIGNUP_PATH}
                className="inline-flex items-center gap-1.5 text-sm font-bold bg-white/10 hover:bg-white/20 text-white px-5 py-3 rounded-2xl transition"
              >
                Start free first
              </Link>
            </div>

            <p className="mt-6 text-xs text-white/60 leading-relaxed">
              All documents currently DRAFT v2026-05 pending outside counsel review.
              Each is publicly accessible at the linked URL — no email gate required.
            </p>
          </div>

          <div className="bg-white/5 p-8 md:p-10 lg:p-12 border-t lg:border-t-0 lg:border-l border-white/10">
            <h3 className="text-sm font-bold uppercase tracking-wider text-white/60">What's inside</h3>
            <ul className="mt-5 space-y-3">
              {PACK_CONTENTS.map((doc) => (
                <li key={doc.name}>
                  <a
                    href={doc.href}
                    target={doc.href.startsWith('mailto:') ? undefined : '_blank'}
                    rel={doc.href.startsWith('mailto:') ? undefined : 'noreferrer'}
                    className="block group rounded-xl border border-white/10 hover:border-white/30 bg-white/0 hover:bg-white/5 px-4 py-3 transition"
                  >
                    <div className="flex items-start gap-3">
                      <FileText className="w-4 h-4 mt-1 text-white/60 group-hover:text-white shrink-0" aria-hidden="true" />
                      <div className="flex-1">
                        <p className="text-sm font-bold text-white">{doc.name}</p>
                        <p className="mt-0.5 text-xs text-white/60">{doc.detail}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 mt-1 text-white/40 group-hover:text-white shrink-0" aria-hidden="true" />
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  PROCUREMENT FAQ  ───────────────────────── */

const FAQ = [
  {
    q: 'How does Jobskills comply with the EU AI Act?',
    a: 'Jobskills B2B was designed assuming Annex III (high-risk AI for recruitment) applies. We don\'t process biometric data, so we sit outside the Art 5(1)(f) emotion-recognition prohibition. AI generates structured questions and writes a summary; your recruiter does the BARS scoring and makes the hire decision — which keeps you outside fully-automated decision-making under GDPR Art 22. Every AI call is logged with cryptographic hashes for tamper-evident audit (Art 12). Versioned candidate consent (Art 50). Customer-accessible DPA, AI System Card, and DPIA template.',
  },
  {
    q: 'Do you have a DPA?',
    a: 'Yes — published at /b2b/dpa and accepted as part of B2B signup. It includes Standard Contractual Clauses Module 2 (EU → US), UK IDTA Addendum, Technical and Organizational Measures annex, sub-processor annex, 72-hour breach notification, DSR assistance, audit rights, and return/delete-at-termination.',
  },
  {
    q: 'Where does the data live?',
    a: 'Application + PostgreSQL hosted on Railway (United States, SCCs Module 2 in place). AI inference via DeepInfra (United States, SCCs Module 2 + contractual training opt-out). TLS in transit, AES-256 at rest, bcrypt password hashing. Audit log retention 18 months; candidate data retention configurable per workspace 6–36 months with automatic deletion. Full sub-processor list at /privacy/sub-processors.',
  },
  {
    q: 'Is there a DPIA on file?',
    a: 'An internal DPIA for B2B AI screening is currently in outside counsel review. A fillable client-side DPIA template is available on request to b2b@jobskills.work — your DPO can complete it for your own Art 35 records.',
  },
  {
    q: 'Do you process biometric or special category data?',
    a: 'No. We do not analyze faces, voices, emotions, or "confidence levels." Screening is text-based: structured questions, structured text answers. Candidate identity is verified via email OTP. This keeps Jobskills outside GDPR Art 9 (special categories) and outside EU AI Act Art 5(1)(f) (workplace emotion recognition prohibition).',
  },
  {
    q: 'How does AI affect candidate rights under GDPR Art 22?',
    a: 'AI outputs are recommendations with reasoning, not automated decisions. Your recruiter scores each candidate using a structured BARS rubric and makes the hiring decision. Independent bar-raiser scoring is available with variance tracking. Candidates can decline AI processing at the consent step (right to object).',
  },
  {
    q: 'What happens when candidates want to access or delete their data?',
    a: 'Candidates can email privacy@jobskills.work or the deploying recruiter (controller). For C2C accounts, in-app export and 30-day grace deletion are already shipping. For B2B candidates, self-service Art 20 / Art 17 endpoints are on the roadmap for Q3 2026; until then DSARs are handled manually within the 30-day GDPR window.',
  },
  {
    q: 'Have you appointed an EU/UK Article 27 representative?',
    a: 'Procurement of a representative (Prighter / EDPO) is in progress and will be appointed before 2 August 2026. The Privacy Policy will be updated with the representative\'s contact details on appointment.',
  },
  {
    q: 'How does Jobskills compare to Interviewer.AI, HireVue, and other AI hiring tools?',
    a: 'See the comparison table above. Short version: we don\'t process biometric data and we don\'t make automated decisions. That keeps us structurally outside the most regulator-exposed parts of the EU AI Act and GDPR, while still delivering structured AI-assisted screening at SMB pricing.',
  },
];

function ProcurementFAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section className="bg-white border-y border-gray-100">
      <div className="max-w-4xl mx-auto px-5 md:px-6 py-16 md:py-24">
        <p className="text-xs font-bold uppercase tracking-wider text-accent">Procurement FAQ</p>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold leading-tight">
          The questions your DPO will ask, answered.
        </h2>
        <p className="mt-5 text-base text-ink-2 leading-relaxed">
          Copy these answers into your RFP responses, vendor questionnaires, and security
          reviews. We refresh this section as the regulation evolves.
        </p>

        <div className="mt-10 space-y-2">
          {FAQ.map((item, idx) => {
            const isOpen = open === idx;
            return (
              <div key={item.q} className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : idx)}
                  aria-expanded={isOpen}
                  className="w-full flex items-start justify-between gap-4 p-5 md:p-6 text-left hover:bg-canvas/50 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                >
                  <span className="text-sm md:text-base font-bold text-ink-1">{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-ink-3 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                {isOpen && (
                  <div className="px-5 md:px-6 pb-5 md:pb-6 text-sm md:text-base text-ink-2 leading-relaxed border-t border-gray-100 pt-4">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────  CTA STRIP  ───────────────────────── */

function CtaStrip() {
  return (
    <section className="max-w-6xl mx-auto px-5 md:px-6 py-16 md:py-24">
      <div className="rounded-3xl bg-accent text-white p-8 md:p-12 text-center">
        <h2 className="text-2xl md:text-4xl font-bold leading-tight max-w-3xl mx-auto">
          Hire smarter. Sleep at night.
        </h2>
        <p className="mt-5 text-base md:text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
          The only AI screening tool built EU AI Act-native from day one. No facial
          scoring, no voice biometrics, no hidden algorithmic decisions.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={SIGNUP_PATH}
            className="inline-flex items-center justify-center gap-1.5 text-sm font-bold bg-white text-accent hover:bg-white/90 px-5 py-3 rounded-2xl transition"
          >
            Start free
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
          <Link
            to={LANDING_PATH}
            className="inline-flex items-center justify-center gap-1.5 text-sm font-bold bg-white/10 hover:bg-white/20 text-white px-5 py-3 rounded-2xl transition"
          >
            See product overview
          </Link>
        </div>
      </div>
    </section>
  );
}
